'use client';

import { ErrorState, LoadingState, PageHeader } from '@/components/shared';
import {
  Badge,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui';
import { InviteUserDialog } from '@/components/users/InviteUserDialog';
import { useOrganizationFleet } from '@/hooks/useAdminVehicles';
import { useUsers } from '@/hooks/useUsers';
import { UnitsTable } from './UnitsTable';

/**
 * One customer: their units and their accounts.
 *
 * This is the far end of an onboarding — the partner's pool hands a device over,
 * and everything after that happens here.
 */
export function OrganizationDetailPage({ organizationId }: { organizationId: string }) {
  const { data: fleet, isLoading, error } = useOrganizationFleet(organizationId);
  const { data: users } = useUsers({ organizationId, limit: 100 });

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState title="Could not load the organisation" message={error.message} />;
  if (!fleet) {
    return <ErrorState title="Organisation not found" message="It may have been deleted." />;
  }

  const { organization } = fleet;

  return (
    <>
      <PageHeader
        backTo="/organizations"
        backLabel="All organizations"
        title={organization.name}
        description={organization.slug}
        actions={
          <Badge variant={organization.isActive ? 'success' : 'secondary'}>
            {organization.isActive ? 'Active' : 'Inactive'}
          </Badge>
        }
      />

      <Tabs defaultValue="units">
        <TabsList>
          <TabsTrigger value="units">Units ({fleet.data.length})</TabsTrigger>
          <TabsTrigger value="users">Users ({users?.pagination.total ?? 0})</TabsTrigger>
        </TabsList>

        <TabsContent value="units" className="pt-4">
          <UnitsTable fleet={fleet} />
        </TabsContent>

        <TabsContent value="users" className="space-y-4 pt-4">
          <div className="flex justify-end">
            <InviteUserDialog organizationId={organization.id} />
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Account</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(users?.data ?? []).map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <p className="font-medium">{user.name ?? 'Unnamed'}</p>
                      <p className="text-sm text-muted-foreground">{user.email}</p>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{user.role}</TableCell>
                    <TableCell>
                      <Badge variant={user.isActive === false ? 'secondary' : 'success'}>
                        {user.isActive === false ? 'Disabled' : 'Active'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </>
  );
}
